import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Input,
  Icon,
  Tooltip,
  message,
  Spin,
  Select,
  Button,
  Popover,
  Alert,
  Card,
  Table,
  Avatar,
  Tabs,
  Collapse
} from 'antd'
import classNames from 'classnames'
import {
  SortableContainer,
  SortableElement,
  sortableHandle,
  arrayMove as arrayMoveImmutable
} from 'react-sortable-hoc'

import StepTitle from '../StepTitle'
import StatusView from '../StatusView/StatusView'

import './ShotList.scss'
import {
  generateShots,
  mapCharacterToShots,
  getAvailableCharacters,
  groupShotsToScenes,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import AddCharacterModal from './components/CharacterMappingTable/AddCharacterModal'
import CharacterMappingTable from './components/CharacterMappingTable'
import LocalReferencesView from './components/LocalReferences'

const { Option } = Select
const { Panel } = Collapse
const { TabPane } = Tabs

// Drag Handle Component
const DragHandle = sortableHandle(() => (
  <Tooltip title="Drag to reorder">
    <div className="drag-handle">
      <Icon type="drag" style={{ fontSize: '18px' }} />
    </div>
  </Tooltip>
))

// Character Highlighter Component (read-only, just highlights)
const CharacterHighlighter = ({ text, selectedCharacters = [] }) => {
  // ... (keeping existing logic for mapping and highlighting)
  const characterMapping = useMemo(() => {
    const mapping = {}
    selectedCharacters.forEach(char => {
      const originalKey =
        (char.original && char.original.replace(/^__|__$/g, '')) || ''
      const currentKey =
        (char.current && char.current.replace(/^__|__$/g, '')) || ''
      if (originalKey && currentKey) {
        mapping[currentKey] = char.original
        mapping[originalKey] = char.original
      }
    })
    return mapping
  }, [selectedCharacters])

  const highlightCharacters = text => {
    if (!text || typeof text !== 'string') return text

    let processedText = text
    const highlights = []

    const placeholderRegex = /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/g
    let match
    while ((match = placeholderRegex.exec(text)) !== null) {
      highlights.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        isPlaceholder: true
      })
    }

    selectedCharacters.forEach(char => {
      if (char.current) {
        const currentName = char.current.replace(/^__|__$/g, '')
        const regex = new RegExp(`\\b${currentName}\\b`, 'g')
        let nameMatch
        while ((nameMatch = regex.exec(text)) !== null) {
          const isOverlapping = highlights.some(
            h => nameMatch.index >= h.start && nameMatch.index < h.end
          )
          if (!isOverlapping) {
            highlights.push({
              start: nameMatch.index,
              end: nameMatch.index + currentName.length,
              text: currentName,
              isPlaceholder: false
            })
          }
        }
      }
    })

    highlights.sort((a, b) => a.start - b.start)

    if (highlights.length === 0) return text

    const parts = []
    let lastIndex = 0

    highlights.forEach((highlight, idx) => {
      if (highlight.start > lastIndex) {
        parts.push(text.substring(lastIndex, highlight.start))
      }

      parts.push(
        <span key={`highlight-${idx}`} className="character-placeholder">
          {highlight.text}
        </span>
      )

      lastIndex = highlight.end
    })

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts
  }

  return (
    <div className="character-highlighter">
      <div className="shot-direction-text">{highlightCharacters(text)}</div>
    </div>
  )
}

// Read-only display for other fields
const ReadOnlyField = ({ text }) => {
  return <div className="readonly-field">{text}</div>
}

// Shot Component (not sortable)
const Shot = ({
  shot,
  shotIndex,
  expandedPanelId,
  setExpandedPanelId,
  selectedCharacters
}) => {
  const { id, title, initial_image_prompt, video_prompt, dialogue } = shot
  const isExpanded = expandedPanelId === id

  return (
    <div className={classNames('shot-list-shot', { expanded: isExpanded })}>
      <div
        className="shot-header"
        onClick={() => setExpandedPanelId(isExpanded ? null : id)}
      >
        <div className="shot-title-container">
          <span>Shot {shotIndex + 1}</span>
          <Icon
            type="right"
            rotate={isExpanded ? 90 : 0}
            className="expand-icon"
          />
        </div>
      </div>

      <div
        className={classNames('shot-description-container', {
          expanded: isExpanded
        })}
      >
        <div className="shot-detail-field">
          <label>Title</label>
          <ReadOnlyField text={title} />
        </div>

        <div className="shot-detail-field">
          <label>Initial Image Prompt</label>
          <Card>
            <CharacterHighlighter
              text={initial_image_prompt}
              selectedCharacters={selectedCharacters}
            />
          </Card>
        </div>

        <div className="shot-detail-field">
          <label>Video Prompt</label>
          <Card>
            <CharacterHighlighter
              text={video_prompt}
              selectedCharacters={selectedCharacters}
            />
          </Card>
        </div>

        {Object.keys(dialogue || {}).length > 0 && (
          <div className="shot-detail-field">
            <label>Dialogue</label>
            <Card>
              {Object.entries(dialogue || {}).map(([character, line], idx) => (
                <div key={idx} className="dialogue-entry readonly">
                  <div className="dialogue-character">
                    <Icon type="message" style={{ fontSize: '12px' }} />
                    <CharacterHighlighter
                      text={character}
                      selectedCharacters={selectedCharacters}
                    />
                  </div>
                  <div className="dialogue-line">
                    <CharacterHighlighter
                      text={typeof line === 'object' ? line.text : line}
                      selectedCharacters={selectedCharacters}
                    />
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

// Sortable Scene Component
const SortableScene = SortableElement(
  ({
    scene,
    sceneIndex,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    return (
      <Card
        className="scene-card"
        title={
          <div
            className="scene-header"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <DragHandle />
            <span className="scene-title">{scene.title.toUpperCase()}</span>
            <Icon
              type="right"
              rotate={isExpanded ? 90 : 0}
              className="scene-expand-icon"
              style={{ marginLeft: 'auto' }}
            />
          </div>
        }
      >
        <div className="scene-info">
          <div className="scene-detail-field">
            <label>Location</label>
            <ReadOnlyField text={scene.description} />
          </div>

          {isExpanded && (
            <div className="scene-shots">
              <h4>Shots ({scene.shots.length})</h4>
              {scene.shots.map((shot, index) => (
                <Shot
                  key={`${scene.id}-shot-${index}`}
                  shot={shot}
                  shotIndex={index}
                  expandedPanelId={expandedPanelId}
                  setExpandedPanelId={setExpandedPanelId}
                  selectedCharacters={selectedCharacters}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    )
  }
)

// Sortable Scene List Component
const SortableSceneList = SortableContainer(
  ({
    scenes,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters,
    isExpanded,
    onToggleExpand
  }) => {
    return (
      <Card
        className="expandable-card"
        title={
          <div className="expandable-card-header" onClick={onToggleExpand}>
            <Icon
              type="right"
              className={classNames('expand-icon', {
                'is-expanded': isExpanded
              })}
            />
            <span className="header-title">Scenes</span>
            <span className="header-count">{scenes.length}</span>
          </div>
        }
        bodyStyle={{
          padding: isExpanded ? '24px' : '0',
          display: isExpanded ? 'block' : 'none',
          overflow: 'visible'
        }}
        style={{ marginBottom: '24px' }}
      >
        <div className="scene-list">
          {scenes.map((scene, index) => (
            <SortableScene
              key={`scene-${index}`}
              index={index}
              scene={scene}
              sceneIndex={index}
              expandedPanelId={expandedPanelId}
              setExpandedPanelId={setExpandedPanelId}
              selectedCharacters={selectedCharacters}
            />
          ))}
        </div>
      </Card>
    )
  }
)

// Custom function to render the dragged element
const dragHandleHelper = (node, parent) => {
  const clone = node.cloneNode(true)

  // Find and remove expansion details during drag
  const shotsContainer = clone.querySelector('.scene-shots')
  if (shotsContainer) {
    shotsContainer.remove()
  }

  clone.className += ' dragging-scene'
  parent.appendChild(clone)

  return clone
}

const MAX_RETRIES = 10

const ShotList = forwardRef((props, ref) => {
  const {
    formData,
    setFormData,
    onValidationChange,
    onProceed,
    onEnableProcessShots,
    onDisableProcessShots,
    onFineTunedLoading,
    isGeneratingLocationReference,
    shotsGenerationProgress,
    onRegenerateLocationReference,
    onAddLocationReference,
    onEditLocationReference,
    onBatchUpdateLocations,
    isClusteringAudio,
    isFetchingCharacters,
    characters: propsCharacters,
    setCharacters: setPropsCharacters,
    fetchCharacters,
    locationGenerationStatus,
    locationGenerationProgress
  } = props

  const [scenes, setScenes] = useState([])
  const [expandedPanelId, setExpandedPanelId] = useState(null)

  // Tab state
  const [activeTab, setActiveTab] = useState('1')
  const [isCharacterMapLoading, setIsCharacterMapLoading] = useState(false)
  // Use characters from props (from useCharacters hook in parent)
  const characters = propsCharacters || []
  const setCharacters = setPropsCharacters
  const [changedCharacters, setChangedCharacters] = useState([])
  const [initialCharacters, setInitialCharacters] = useState([])
  const [apiError, setApiError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [errorLog, setErrorLog] = useState(null)

  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false)
  const [clustering, setIsClustering] = useState(false)

  const [isScenesExpanded, setIsScenesExpanded] = useState(true)
  const [showLocationModal, setShowLocationModal] = useState(false)

  const {
    characterList = [],
    shotList = [],
    shotLoading = false,
    shotsApiRes = {},
    selectedCharacter = []
  } = formData

  useEffect(() => {
    // Characters now come from props via useCharacters hook in parent
    // Just sync local UI state with props
    if (selectedCharacter && selectedCharacter.length > 0) {
      setChangedCharacters(selectedCharacter)
      setInitialCharacters(selectedCharacter)
    }

    if (shotList.length > 0) {
      setScenes(shotList)
      setFormData(prev => ({ ...prev, shotLoading: false }))
    } else if (shotLoading) {
      return
    }
  }, [])

  useEffect(() => {
    if (shotList.length > 0) {
      setScenes(shotList)
      setFormData(prev => ({ ...prev, shotLoading: false }))
    }
  }, [shotList])

  useEffect(() => {
    if (characters.length > 0) {
      setFormData({ ...formData, characterList: characters })
    }
  }, [characters])

  useEffect(() => {
    if (changedCharacters.length > 0) {
      setFormData({ ...formData, selectedCharacter: changedCharacters })
    }
  }, [changedCharacters])

  useImperativeHandle(ref, () => ({
    processShots: () => {
      generateFineTunedShots()
    }
  }))

  // Enhanced function to extract characters from both original and current text
  function extractCharacters(scenesArray) {
    const characterSet = new Set()

    // Regex to match both **character1** and __character1__ formats
    const characterRegex = /(?:\*\*character\d+\*\*|__character\d+__)/g

    function searchInValue(value) {
      if (typeof value === 'string') {
        const matches = value.match(characterRegex)
        if (matches) {
          matches.forEach(match => characterSet.add(match))
        }
      } else if (Array.isArray(value)) {
        // Handle arrays
        value.forEach(item => searchInValue(item))
      } else if (typeof value === 'object' && value !== null) {
        // Handle objects
        Object.values(value).forEach(val => searchInValue(val))
      }
    }

    // Search through the entire scenes array
    searchInValue(scenesArray)

    return Array.from(characterSet)
  }

  // Enhanced character change handler that works from CharacterMappingTable
  const handleCharacterChange = (
    oldCharacter,
    newCharacter,
    _firstCharacter
  ) => {
    console.log(`Replacing "${oldCharacter}" with "${newCharacter}"`)

    const oldCharWithUnderscores = `__${oldCharacter.replace(/^__|__$/g, '')}__`
    const newCharWithUnderscores = `__${newCharacter}__`

    // Update character mappings
    let updatedChars = [...changedCharacters]
    const existingCharIndex = updatedChars.findIndex(
      char =>
        char.original === oldCharWithUnderscores ||
        (char.original &&
          char.original.replace(/^__|__$/g, '') ===
            oldCharacter.replace(/^__|__$/g, ''))
    )

    if (existingCharIndex !== -1) {
      updatedChars[existingCharIndex] = {
        ...updatedChars[existingCharIndex],
        last: updatedChars[existingCharIndex].current,
        current: newCharWithUnderscores
      }
    } else {
      updatedChars.push({
        original: oldCharWithUnderscores,
        last: oldCharWithUnderscores,
        current: newCharWithUnderscores
      })
    }

    setChangedCharacters(updatedChars)
    message.success(
      `Changed "${oldCharacter}" to "${newCharacter}" throughout all scenes and shots`
    )
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedScenes = arrayMoveImmutable(scenes, oldIndex, newIndex)
    setScenes(updatedScenes)
    setFormData(prev => ({ ...prev, shotList: updatedScenes }))
  }

  // Get total characters including those from original shots and selectedCharacter
  const totalCharacters = useMemo(() => {
    const originalChars = extractCharacters(shotList)
    return Array.from(new Set(originalChars))
  }, [shotList])

  const handleFineTunedShotsResponse = shotsData => {
    setFormData(prev => ({ ...prev, shotLoading: true }))

    const processedShotsData = { ...shotsData }

    Object.keys(processedShotsData).forEach(shotKey => {
      const shot = processedShotsData[shotKey]
      const charactersInShot = new Set()

      if (shot['initial image prompt'] || shot.initial_image_prompt) {
        const imgPromptChars = extractCharactersFromText(
          shot['initial image prompt'] || shot.initial_image_prompt
        )
        imgPromptChars.forEach(char => charactersInShot.add(char))
      }

      if (shot['video prompt'] || shot.video_prompt) {
        const videoPromptChars = extractCharactersFromText(
          shot['video prompt'] || shot.video_prompt
        )
        videoPromptChars.forEach(char => charactersInShot.add(char))
      }

      if (shot.dialogue) {
        Object.keys(shot.dialogue).forEach(character => {
          const cleanCharacter = character.replace(
            /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/,
            '$2'
          )
          charactersInShot.add(cleanCharacter)
        })

        Object.values(shot.dialogue).forEach(line => {
          const text = typeof line === 'object' ? line.text : line
          if (text) {
            const dialogueCharacters = extractCharactersFromText(text)
            dialogueCharacters.forEach(char => charactersInShot.add(char))
          }
        })
      }

      processedShotsData[shotKey].characters = Array.from(charactersInShot)
    })

    handleScenesMapping(processedShotsData, true)
  }

  const extractCharactersFromText = text => {
    // Return an empty array if the input is not a valid string.
    if (!text || typeof text !== 'string') {
      return []
    }

    // This regex specifically looks for the pattern '--character' followed by digits, and then '--'.
    const characterRegex = /--character\d+--/g

    // String.prototype.match() with a global regex returns an array of all matches or null.
    const matches = text.match(characterRegex)

    // If matches are found, return a unique array of them. Otherwise, return an empty array.
    return matches ? Array.from(new Set(matches)) : []
  }

  const formatSceneObj = sceneObj => {
    return Object.entries(sceneObj).map(([sceneKey, sceneValue]) => {
      const { scene_location = '', shots = [] } = sceneValue

      const formattedShots = shots.map((shotObj, index) => {
        const shotKey = Object.keys(shotObj)[0]
        const shotValue = shotObj[shotKey]

        return {
          id: `${sceneKey}-shot-${index + 1}`,
          key: shotKey,
          title: shotValue.title || '',
          initial_image_prompt:
            shotValue['initial image prompt'] ||
            shotValue.initial_image_prompt ||
            '',
          video_prompt:
            shotValue['video prompt'] || shotValue.video_prompt || '',
          dialogue: shotValue.dialogue || {},
          characters: shotValue.characters || []
        }
      })

      return {
        id: sceneKey,
        title: sceneKey,
        description: scene_location,
        shots: formattedShots
      }
    })
  }

  const generateFineTunedShots = () => {
    onFineTunedLoading && onFineTunedLoading()

    const characterDict = changedCharacters.reduce((acc, char) => {
      const cleanedName = char.current.replace(/^__|__$/g, '')
      const characterData = characters.find(
        c => c.name.toLowerCase() === cleanedName.toLowerCase()
      )

      acc[char.original] = {
        name: cleanedName,
        gender: characterData ? characterData.gender : 'unknown'
      }

      return acc
    }, {})

    const payload = {
      script: shotsApiRes || formData.shotsApiRes,
      character_dict: characterDict
    }

    handleCharacterMapping(payload)
  }

  const handleCharacterMapping = async payload => {
    try {
      setIsCharacterMapLoading(true)
      const res = await mapCharacterToShots(payload)
      const newShots = res.data && res.data.modified_script

      handleFineTunedShotsResponse(newShots)
    } catch (error) {
      onFineTunedLoading && onFineTunedLoading()
      message.error('Character mapping failed. Please Try Again !')
      console.error('Character mapping failed:', error)
    } finally {
      setIsCharacterMapLoading(false)
      setChangedCharacters([])
      onValidationChange(true)
    }
  }

  const handleScenesMapping = async (data, isFineTuned) => {
    try {
      setIsClustering(true)
      const payload = {
        input_json_data: data
      }

      const response = await groupShotsToScenes(payload)

      // Always expect a task id and poll until completion
      const taskId =
        response &&
        response.data &&
        (response.data.task_id ||
          response.data['task-id'] ||
          response.data.taskId)
      if (!taskId) {
        throw new Error('Missing task_id in scene grouping response')
      }

      await pollSceneTaskStatus(taskId, data, !!isFineTuned)
    } catch (error) {
      message.error('Scene mapping failed. Please Try Again !')
      console.error('Scene mapping failed:', error)
      setFormData(prev => ({ ...prev, shotLoading: false }))
      setIsClustering(false)
    }
  }

  const pollSceneTaskStatus = async (
    taskId,
    originalData,
    isFineTuned,
    retryCount = 0
  ) => {
    try {
      const response = await getTaskStatus(taskId)

      if (
        response &&
        response.data &&
        response.data.status === 'success' &&
        response.data.output
      ) {
        const sceneData = response.data.output
        const formattedSceneData = formatSceneObj(sceneData)

        setScenes(formattedSceneData)
        setFormData(prev => ({
          ...prev,
          shotList: formattedSceneData,
          shotsApiRes: originalData
        }))

        setFormData(prev => ({ ...prev, shotLoading: false }))
        setIsClustering(false)
        if (isFineTuned) {
          onProceed()
        }
      } else if (
        response &&
        response.data &&
        response.data.status === 'failed'
      ) {
        console.error('Task failed:', response.data)
        message.error('Scene mapping failed. Please Try Again !')
        setFormData(prev => ({ ...prev, shotLoading: false }))
        setIsClustering(false)
      } else if (retryCount < MAX_RETRIES) {
        setTimeout(function() {
          pollSceneTaskStatus(taskId, originalData, isFineTuned, retryCount + 1)
        }, 2000)
      } else {
        console.error('Max retries exceeded for task:', taskId)
        message.error('Scene mapping timeout. Please Try Again !')
        setFormData(prev => ({ ...prev, shotLoading: false }))
        setIsClustering(false)
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(function() {
          pollSceneTaskStatus(taskId, originalData, isFineTuned, retryCount + 1)
        }, 2000)
      } else {
        console.error('Error polling task status:', error)
        message.error('Error while polling scene mapping. Please Try Again !')
        setFormData(prev => ({ ...prev, shotLoading: false }))
        setIsClustering(false)
      }
    }
  }

  const isAllCharactersChanged =
    totalCharacters.length &&
    changedCharacters.length === totalCharacters.length

  const hasChanges = useMemo(() => {
    if (changedCharacters.length !== initialCharacters.length) return true

    const normalize = arr =>
      arr
        .slice()
        .sort((a, b) => a.original.localeCompare(b.original))
        .map(({ original, current }) => ({ original, current }))

    const a = normalize(changedCharacters)
    const b = normalize(initialCharacters)

    return JSON.stringify(a) !== JSON.stringify(b)
  }, [changedCharacters, initialCharacters])

  useEffect(() => {
    console.log({ isAllCharactersChanged, length: changedCharacters.length })
    if (changedCharacters.length && isAllCharactersChanged) {
      onValidationChange(prev => ({ ...prev, shotList: true }))
    }
  }, [isAllCharactersChanged, changedCharacters])

  const handleAddCharacter = () => {
    setShowAddCharacterModal(true)
  }

  const handleCharacterAddition = _character => {
    // Refresh characters from the parent hook
    if (fetchCharacters) {
      fetchCharacters(true) // force refresh
    }
  }

  console.log({ isGeneratingLocationReference, clustering })

  if (shotLoading || isFetchingCharacters || isCharacterMapLoading) {
    const loadingTitle = isFetchingCharacters
      ? 'Fetching characters...'
      : isCharacterMapLoading
      ? 'Mapping characters to shots...'
      : `Generating your shots${retryCount ? ` (Retry ${retryCount})` : ''}...`

    const loadingProgress =
      isFetchingCharacters || isCharacterMapLoading
        ? null
        : isGeneratingLocationReference
        ? locationGenerationProgress
        : shotsGenerationProgress

    return (
      <div className="shot-list-shots">
        <StatusView
          status="loading"
          title={loadingTitle}
          subTitle="Please wait! This may take a few minutes."
          progress={loadingProgress}
          inline
        />
        {retryCount > 0 && errorLog && (
          <div
            style={{ textAlign: 'center', marginTop: -20, marginBottom: 40 }}
          >
            <blockquote
              style={{
                color: 'gray',
                fontSize: '0.8rem',
                display: 'inline-block'
              }}
            >
              Last Error: {errorLog}
            </blockquote>
          </div>
        )}
      </div>
    )
  }

  console.log(
    'characters',
    shotsApiRes,
    shotList,
    characters,
    changedCharacters
  )

  return (
    <div className="shot-list-shots">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px'
        }}
      >
        <StepTitle
          iconType="ordered-list"
          title="Shot List"
          description="Map characters first, then drag to reorder scenes."
          isGeneratingLocationReference={isGeneratingLocationReference}
        />

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            type="primary"
            onClick={props.onRegenerateShots}
            loading={shotLoading}
            icon="reload"
            size="large"
          >
            Regenerate Shots
          </Button>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginTop: 16 }}
      >
        <TabPane
          tab={
            <span>
              {!isAllCharactersChanged && <Icon type="warning" />}
              Shot List
            </span>
          }
          key="1"
        >
          {totalCharacters.length > 0 && (
            <CharacterMappingTable
              placeholderCharacters={totalCharacters}
              characters={characters}
              onCharacterChange={handleCharacterChange}
              changedCharacters={changedCharacters}
              onAddCharacter={handleAddCharacter}
              isFetchingCharacters={isFetchingCharacters}
              characterMap={formData.characterMap || {}}
              isAllCharactersChanged={isAllCharactersChanged}
            />
          )}

          <SortableSceneList
            scenes={scenes}
            expandedPanelId={expandedPanelId}
            setExpandedPanelId={setExpandedPanelId}
            characters={characters}
            onCharacterChange={handleCharacterChange}
            onSortEnd={onSortEnd}
            useDragHandle
            axis="y"
            helperClass="dragging-scene"
            helperContainer={document.body}
            helper={dragHandleHelper}
            isExpanded={isScenesExpanded}
            onToggleExpand={() => setIsScenesExpanded(!isScenesExpanded)}
            selectedCharacters={changedCharacters}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              {isGeneratingLocationReference && <Icon type="loading" />}
              Locations
            </span>
          }
          key="2"
        >
          {(formData.locationMap || formData.locationReference) && (
            <LocalReferencesView
              locationMap={formData.locationMap || {}}
              locationReference={formData.locationReference || {}}
              scenes={scenes}
              setFormData={setFormData}
              onRegenerateLocationReference={onRegenerateLocationReference}
              onAddLocationReference={onAddLocationReference}
              onEditLocationReference={onEditLocationReference}
              isGeneratingLocationReference={isGeneratingLocationReference}
              onBatchUpdateLocations={onBatchUpdateLocations}
              locationGenerationProgress={locationGenerationProgress}
              locationGenerationStatus={locationGenerationStatus}
            />
          )}
        </TabPane>

        <TabPane tab="Story" key="3">
          <Card title="Generated Story" style={{ marginBottom: '20px' }}>
            {formData.voiceoverText ? (
              <div>
                <p>{formData.voiceoverText}</p>
              </div>
            ) : (
              <p style={{ color: '#999' }}>No story text available.</p>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {showAddCharacterModal && (
        <AddCharacterModal
          onClose={() => setShowAddCharacterModal(false)}
          onCreate={handleCharacterAddition}
        />
      )}
    </div>
  )
})

export default ShotList
